/* SEC_HEADER v1 — Sticky-Logik + Mobile-Announcement-Carousel
 * - Sticky ab scrollY > 50 (über IntersectionObserver auf Sentinel)
 * - Hide bei Scroll-Down nach >200px Tiefe
 * - Reveal bei Scroll-Up
 * - Kein blocking JS, init erst bei DOMContentLoaded
 */

(function () {
  'use strict';

  const HIDE_TRIGGER_PX = 200;
  const SCROLL_DELTA = 4;

  /* [STICKY_REVEAL] — Robust implementation.
   * Rules: scrollY > 200 + scroll-down → hide. scroll-up → reveal. scrollY ≤ 200 → always visible.
   * Announcement bar is a sibling, not part of this group — unaffected.
   * prefers-reduced-motion: reduce → sticky but never hides.
   */
  function initStickyHeader() {
    const group = document.querySelector('[data-smelu-header-group]');
    if (!group) return;

    if (group.dataset.sticky !== 'true') return;

    group.classList.add('smelu-header-group--sticky');

    const headerEl = group.querySelector('.smelu-header');

    function updateMeasurements() {
      if (headerEl) {
        document.documentElement.style.setProperty('--smelu-header-h', headerEl.offsetHeight + 'px');
      }
    }
    updateMeasurements();
    window.addEventListener('resize', updateMeasurements, { passive: true });

    // prefers-reduced-motion: sticky but no hide/show animation
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Hide-on-Scroll-Effekt: nur aktiv wenn data-hide-on-scroll="true".
    // Liquid setzt das nur auf der Startseite (template.name == 'index').
    // Auf PDP/Collection/etc. bleibt der Header beim Scrollen ganz stabil.
    const hideOnScroll = group.dataset.hideOnScroll === 'true';

    let lastScrollY = window.pageYOffset || document.documentElement.scrollTop;
    let ticking = false;
    let isHidden = false;
    let isScrolled = false;

    // Ab dem ersten Pixel Scroll wird umgeschaltet. Wichtig: die fixierten
    // Positionen (Leiste 0, Header darunter) entsprechen exakt den Flow-Positionen
    // bei scrollY 0 -- dadurch gibt es beim Umschalten keinen sichtbaren Sprung.
    const SCROLL_FIX_THRESHOLD = 0;

    function show() {
      if (isHidden) {
        group.classList.remove('is-hidden');
        group.classList.add('is-revealed');
        isHidden = false;
      }
    }

    function hide() {
      if (!isHidden) {
        group.classList.add('is-hidden');
        group.classList.remove('is-revealed');
        isHidden = true;
      }
    }

    function enterScrolled() {
      if (!isScrolled) {
        group.classList.add('is-scrolled');
        document.body.classList.add('smelu-header-fixed');
        isScrolled = true;
      }
    }

    function exitScrolled() {
      if (isScrolled) {
        group.classList.remove('is-scrolled');
        group.classList.remove('is-hidden');
        group.classList.remove('is-revealed');
        document.body.classList.remove('smelu-header-fixed');
        isScrolled = false;
        isHidden = false;
      }
    }

    function onScroll() {
      const currentY = window.pageYOffset || document.documentElement.scrollTop;
      const delta = currentY - lastScrollY;

      // Header wird fixed sobald über Trust-Bar gescrollt
      if (currentY > SCROLL_FIX_THRESHOLD) {
        enterScrolled();
      } else {
        exitScrolled();
      }

      // Hide/show nur wenn fixed UND hide-on-scroll-Effekt erlaubt ist
      if (hideOnScroll && isScrolled && Math.abs(delta) >= SCROLL_DELTA) {
        if (currentY <= HIDE_TRIGGER_PX) {
          show();
        } else if (delta > 0) {
          hide();
        } else {
          show();
        }
      }

      lastScrollY = currentY;
      ticking = false;
    }

    function onScrollRequest() {
      if (!ticking) {
        window.requestAnimationFrame(onScroll);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScrollRequest, { passive: true });

    // Beim Laden mit wiederhergestellter Scroll-Position sofort den
    // richtigen Zustand setzen, statt auf das erste Scroll-Event zu warten.
    onScroll();
  }

  /* [STICKY_SALEBAR] Sale-/Trust-Leiste bleibt beim Scrollen oben stehen.
   * Bewusst unabhaengig vom Header: der Header ist nur auf der Startseite
   * sticky (template.name == 'index'), die Leiste soll aber ueberall
   * mitfahren -- auch auf der Produktseite.
   *
   * position:sticky scheidet aus, weil Shopify jede Section in einen eigenen
   * <div id="shopify-section-..."> wickelt. Dieser Wrapper ist der Containing
   * Block und nur so hoch wie die Leiste selbst, also scrollt sie trotz sticky
   * mit weg. Deshalb: position:fixed plus ein Spacer, der im Flow exakt die
   * verlassene Hoehe offen haelt -- so springt beim Fixieren nichts.
   */
  function initStickyAnnouncement() {
    const bar = document.querySelector('[data-smelu-announcement-section]');
    const root = document.documentElement;

    if (!bar || !bar.classList.contains('smelu-announcement-section--sticky')) {
      root.style.setProperty('--smelu-announcement-h', '0px');
      return;
    }

    const spacer = document.createElement('div');
    spacer.className = 'smelu-announcement-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    // Inline-Styles, nicht nur Klasse: Dawns base.css setzt
    // "div:empty { display: none }" und der Spacer ist zwangslaeufig leer.
    // Inline schlaegt jedes Stylesheet, also kollabiert er auch dann nicht,
    // wenn ein Browser noch eine alte section-header.css im Cache hat.
    spacer.style.display = 'block';

    let letzteHoehe = -1;
    function measure() {
      const h = bar.offsetHeight;
      // Nur schreiben, wenn sich wirklich etwas geaendert hat. Der ResizeObserver
      // feuert sonst im Sekundentakt mit, weil im Countdown die Ziffern wechseln --
      // und jedes Setzen der Spacer-Hoehe stoesst ein Relayout der ganzen Seite an.
      if (h === letzteHoehe) return;
      letzteHoehe = h;
      root.style.setProperty('--smelu-announcement-h', h + 'px');
      spacer.style.height = h + 'px';
    }

    measure();
    if (bar.parentNode) bar.parentNode.insertBefore(spacer, bar.nextSibling);
    bar.classList.add('is-fixed');

    window.addEventListener('resize', measure, { passive: true });
    if (window.ResizeObserver) {
      new ResizeObserver(measure).observe(bar);
    }
  }

  function initAnnouncementRotator() {
    const bars = document.querySelectorAll(
      '.smelu-announcement[data-mobile-rotate="true"]'
    );

    bars.forEach(function (bar) {
      const claims = bar.querySelectorAll('.smelu-announcement__claim');
      if (claims.length < 2) return;

      // Mobile-Only-Carousel: nur aktiv wenn Viewport <990px
      const mq = window.matchMedia('(max-width: 989px)');
      let intervalId = null;
      let activeIdx = 0;
      const speedMs = parseInt(bar.dataset.rotateSpeed, 10) || 5000;

      function setActive(idx) {
        claims.forEach(function (c, i) {
          c.classList.toggle('is-active', i === idx);
        });
      }

      function start() {
        setActive(activeIdx);
        if (intervalId) clearInterval(intervalId);
        intervalId = setInterval(function () {
          activeIdx = (activeIdx + 1) % claims.length;
          setActive(activeIdx);
        }, speedMs);
      }

      function stop() {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        // Desktop: alle sichtbar — durch CSS-Override geregelt
        claims.forEach(function (c) {
          c.classList.remove('is-active');
        });
      }

      function sync() {
        if (mq.matches) start();
        else stop();
      }

      sync();
      if (mq.addEventListener) {
        mq.addEventListener('change', sync);
      } else if (mq.addListener) {
        mq.addListener(sync);
      }

      // Pause on hover/focus für Accessibility
      bar.addEventListener('mouseenter', function () {
        if (intervalId) clearInterval(intervalId);
      });
      bar.addEventListener('mouseleave', function () {
        if (mq.matches) start();
      });
    });
  }

  function init() {
    // Zuerst die Leiste: sie setzt --smelu-announcement-h, das der Header
    // fuer seinen Fixed-Offset braucht.
    initStickyAnnouncement();
    initStickyHeader();
    initAnnouncementRotator();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
